import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePortalRuntime } from "../lib/portal-runtime";
import { canReadClaimEvidence } from "../lib/claim-evidence-queries";
import { getDemoInvestigation, type DemoInvestigation } from "../lib/demo-investigation-scenarios";

export function DemoInvestigationSummary({ record }: { record: DemoInvestigation }) {
  const m = record.metrics;
  return (
    <section aria-label="Synthetic executive snapshot" className="space-y-2">
      <h3 className="font-semibold">Synthetic executive snapshot</h3>
      <p>
        {record.id} · {record.outcome.replaceAll("_", " ")}
      </p>
      <p>
        Population: {m.populationId} · {m.unitsPerWindow.toLocaleString()} units per window
      </p>
      <p>
        Baseline: {m.baselineClaims} simulated claims ({m.baselineWindow})
      </p>
      <p>
        Post-change:{" "}
        {m.postClaims === null
          ? "Unavailable — validation pending"
          : `${m.postClaims} simulated claims`}{" "}
        ({m.postWindow})
      </p>
      <p>Target: at most {m.targetClaims} simulated claims. Approved savings: unavailable.</p>
      <p>
        No live financial actual, safety disposition, management approval or customer outcome is
        asserted.
      </p>
    </section>
  );
}

/** Explicit shared selection, keyed by Division and cleared by existing sign-out cache clear. */
export function DemoInvestigationJourney({ summaryOnly = false }: { summaryOnly?: boolean }) {
  const { tenantContext } = usePortalRuntime();
  const cache = useQueryClient();
  const key = ["portal", "demo-investigation", tenantContext.tenantId, "selection"] as const;
  const selection = useQuery<string | null>({
    queryKey: key,
    queryFn: () => null,
    enabled: false,
    initialData: null,
    gcTime: Infinity,
  });
  const allowed =
    tenantContext.permissions.includes("problem_record.read") &&
    canReadClaimEvidence(tenantContext);
  const record = allowed && selection.data ? getDemoInvestigation(selection.data) : null;
  return (
    <section
      aria-label="Separate synthetic demonstration"
      className="rounded-lg border border-border bg-card p-5 space-y-4"
    >
      <h2 className="font-semibold">Separate synthetic demonstration</h2>
      <p className="text-sm">
        Historical discovery and this fictional investigation are independent. Selecting an example
        does not link Ford/Nissan claims, replace failed live reads or change the live evidence
        selection.
      </p>
      {!allowed ? (
        <p role="status">
          Demonstration access requires problem_record.read, evidence.read and an explicit evidence
          scope for this Division. No example was substituted.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => cache.setQueryData(key, "DEMO-JOURNEY-CLOSED")}
              className="rounded border border-border px-3 py-2"
            >
              Open separate synthetic completed example
            </button>
            <button
              type="button"
              onClick={() => cache.setQueryData(key, "DEMO-JOURNEY-PENDING")}
              className="rounded border border-border px-3 py-2"
            >
              Open separate synthetic pending example
            </button>
            <button
              type="button"
              onClick={() => cache.setQueryData(key, null)}
              className="rounded border border-border px-3 py-2"
            >
              Leave synthetic example
            </button>
          </div>
          {record ? (
            <>
              <p role="status" className="font-medium">
                SYNTHETIC · DEMONSTRATION ONLY · READ ONLY — {record.notice}
              </p>
              {!summaryOnly && (
                <>
                  <h3 className="font-semibold">{record.title}</h3>
                  <p>
                    {record.simulatedClaimIds.length} baseline and{" "}
                    {record.simulatedPostClaimIds.length} post-window synthetic evidence identities.
                    Historical claim links: none.
                  </p>
                  <ol className="space-y-3">
                    {record.stages.map((stage) => (
                      <li key={stage.key} className="rounded border border-border p-3">
                        <h4 className="font-semibold">
                          {stage.label} · {stage.state.replaceAll("_", " ")}
                        </h4>
                        <p>{stage.evidence}</p>
                        <p className="text-sm text-muted-foreground">
                          {stage.evidenceId} · {stage.owner} / {stage.reviewer} (simulated roles)
                        </p>
                      </li>
                    ))}
                  </ol>
                </>
              )}
              <DemoInvestigationSummary record={record} />
            </>
          ) : (
            <p role="status">No synthetic example selected. Live evidence remains separate.</p>
          )}
        </>
      )}
    </section>
  );
}
